import '@css/Menu.css'
import '@css/Yacht.css'

import useGalleryApi from '@api/useGalleryApi'
import useTripApi from '@api/useTripApi'
import PdfIcon from '@assets/images/navigation_icons/pdf.svg'
import { getPages, getPDFs, isPageDisabled } from '@config'
import type { MenuButton } from '@constants/activeMenuPages'
import { BUTTON_KEYS, getEnabledMenuPages, getRoute, links } from '@constants/activeMenuPages'
import type { ROUTES } from '@constants/app_routes'
import { hasDashboard } from '@constants/routes_helpers'
import type { ITrip } from '@constants/types'
import AboutYacht from '@containers/AboutYacht'
import type { MediaGallery } from '@containers/admin/types'
import { getServerUrl } from '@hooks/useApi'
import { Button, Icon, Tabs, Tooltip } from '@mui/material'
import { isNonEmptyArray } from '@shared_utils/array_utils'
import type { CSSProperties } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import ScreenLockerButton from './ScreenLockerIcon'

const tabsClasses = {
  indicator: 'mui_tab_indication',
  scrollButtons: 'mui_scroll_buttons'
}

const navLinkStyle: CSSProperties = {
  backgroundColor: 'transparent!important',
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}

export default function Menu () {
  const location = useLocation()
  const { fetchCurrentTrip } = useTripApi()
  const enabledPages = getEnabledMenuPages()
  const { fetchGalleries } = useGalleryApi()
  const enabledPageHrefs = enabledPages.map(page => page.active)

  const [menuValue, setMenuValue] = useState(0)
  const [menuButtons, setMenuButtons] = useState<MenuButton[]>([])
  const [enabledButtons, setEnabledButtons] = useState<MenuButton[]>([])
  const [shouldShowHomeButton, setShouldShowHomeButton] = useState(true)
  const [currentTrip, setCurrentTrip] = useState<ITrip | null>(null)
  const [galleries, setGalleries] = useState<MediaGallery[]>([])
  const isUnmounted = useRef(false)

  const pathname = location.pathname as ROUTES

  const handleChangeMenu = (index: number) => setMenuValue(index)

  const pdfLinks = getPDFs().map((pdf, index) => {
    const { title, icon } = pdf

    return {
      icon: icon ? getServerUrl(icon) : PdfIcon,
      title,
      active: `/pdf/${index}`,
      to: `/pdf/${index}`,
      pageName: 'pdf',
      disabled: isPageDisabled('pdf')
    }
  })

  const infoChannelLinks = links.filter(link => link.pageName === BUTTON_KEYS.INFO_CHANNEL)

  // first show links other than info_channel, then add all PDFs, then add info_channel
  const allLinks = links
    .filter(link => link.pageName !== BUTTON_KEYS.INFO_CHANNEL)
    .concat(pdfLinks)
    .concat(infoChannelLinks)

  const getClass = (active: string) => (pathname.includes(active) ? 'bottom-nav-active' : '')

  useEffect(() => {
    const detailPage = getPages().filter(page => page.parent === BUTTON_KEYS.DETAILS && !page.disabled)[1]

    const buttons = allLinks
      .filter(link => {
        return link.pageName === 'yacht_page' ? !isPageDisabled(BUTTON_KEYS.DETAILS) : true
      })
      .map(link => {
        const to = link.pageName === 'yacht_page' && detailPage ? getRoute(detailPage.page) : link.to
        return { ...link, to }
      })

    setMenuButtons(buttons)
    setShouldShowHomeButton(!isPageDisabled('home'))

    fetchCurrentTrip().then(setCurrentTrip)

    fetchGalleries().then(setGalleries)

    return () => {
      isUnmounted.current = true
    }
  }, [])

  useEffect(() => {
    if (isUnmounted.current) {
      return // ignore this action
    }

    const newEnabledButtons = menuButtons.filter(btn => {
      const { disabled, pageName, active } = btn

      if (disabled) {
        return false
      } else if (!enabledPageHrefs.includes(active) && pageName !== 'pdf') {
        return false
      } else if (!currentTrip && (pageName === BUTTON_KEYS.CALENDAR || pageName === BUTTON_KEYS.TRIP)) {
        return false // don't show trip-related buttons when not in trip
      } else if (!galleries.length && pageName === BUTTON_KEYS.GALLERY) {
        return false
      }

      return true
    })

    setEnabledButtons(newEnabledButtons)
  }, [menuButtons, currentTrip, galleries])

  useEffect(() => {
    if (isUnmounted.current) {
      return // ignore this action
    }

    const maxIcons = window.innerHeight < 900 ? 8 : 12 // 8 icons for "small" devices like iPad
    const numIcons = Math.min(enabledButtons.length, maxIcons) // display no more icons than we can fit
    document.documentElement.style.setProperty('--menu-items', `${numIcons}`)
  }, [enabledButtons.length])

  if (pathname.startsWith('/admin')) {
    return null
  }

  const createMenuButton = (btn: MenuButton, index: number) => {
    return <Tooltip key={index} title={btn.title}>
      <>
        <NavLink
          style={navLinkStyle}
          className={getClass(btn.active)}
          to={btn.to}
          draggable={false}
          onClick={() => handleChangeMenu(index)}
        >
          <Button className="menu__btn">
            <Icon className="h-100">
              <img alt={btn.title} src={btn.icon} className="h-100" style={{ objectFit: 'contain' }} />
            </Icon>
          </Button>
        </NavLink>
      </>
    </Tooltip>
  }
  return <>
    {pathname.startsWith('/details') && <AboutYacht />}

    {shouldShowHomeButton && !hasDashboard(pathname) && <ScreenLockerButton />}

    {isNonEmptyArray(enabledButtons) && <Tabs
      variant="scrollable"
      className="nav"
      value={menuValue}
      classes={tabsClasses}
      sx={{
        '& .MuiTabs-list': {
          width: '100%'
        },
      }}
    >
      {enabledButtons.map((btn, i) => createMenuButton(btn, i))}
    </Tabs>}
  </>
}
